import { usePracticeReviewFunnel, usePracticeTextFunnel } from '../../hooks';
import type { FunnelRange } from '../../api/commandCenterApi';
import {
  AnalyticsCard,
  RangeSwitch,
  WeeklyByMethodCard,
} from '@/modules/reviews/analytics/AnalyticsCards';
import { percent, usd } from '@/modules/reviews/analytics/format';
import a from '@/modules/reviews/analytics/analytics.module.css';

/**
 * Link & QR vs text for the same practice and range. A scan is a patient who already chose to
 * start; a text reaches everyone with a confirmed visit, so the rates differ by design and the
 * absolute numbers tell the story.
 */
export function CompareMethodsPanel({
  practiceId,
  locationId,
  range,
  onRangeChange,
}: {
  practiceId: string;
  locationId?: string;
  range: FunnelRange;
  onRangeChange: (r: FunnelRange) => void;
}) {
  const scope = { range, ...(locationId ? { locationId } : {}) };
  const qr = usePracticeReviewFunnel(practiceId, { ...scope, channel: 'manual_qr' });
  const text = usePracticeTextFunnel(practiceId, scope);

  const toolbar = (
    <div className={a.toolbar}>
      <div>
        <h3 className={a.cardTitle}>Link &amp; QR vs. text</h3>
        <p className={a.cardSub}>Same practice, same {range}</p>
      </div>
      <RangeSwitch value={range} onChange={onRangeChange} />
    </div>
  );
  if (qr.isLoading || text.isLoading)
    return (
      <div className={a.stack}>
        {toolbar}
        <p className={a.muted}>Loading…</p>
      </div>
    );
  if (!qr.data || !text.data)
    return (
      <div className={a.stack}>
        {toolbar}
        <p className={a.muted}>Could not load both funnels.</p>
      </div>
    );

  const q = qr.data;
  const t = text.data;
  const k = t.kpis;
  const scans = q.steps[0]?.count ?? 0;
  const qrRated = q.steps.find((s) => s.step === 'rating')?.count ?? 0;
  const qrConfirmed = q.outcomes.postConfirmed;
  const qrMatched = q.outcomes.matched ?? 0;
  const rows: Array<[string, string, string]> = [
    ['Patients reached', `${scans} scans`, `${k.texted} texted`],
    [
      'Gave a rating',
      `${qrRated} (${percent(qrRated, scans)})`,
      `${k.rated} (${percent(k.rated, k.texted)})`,
    ],
    [
      'Gave 5★',
      `${q.branch.fiveStar} (${percent(q.branch.fiveStar, scans)})`,
      `${k.fiveStar} (${percent(k.fiveStar, k.texted)})`,
    ],
    [
      'Tapped Open Google',
      `${q.outcomes.posted} (${percent(q.outcomes.posted, scans)})`,
      `${k.tappedGoogle} (${percent(k.tappedGoogle, k.texted)})`,
    ],
    [
      'Confirmed posted by patient',
      `${qrConfirmed} (${percent(qrConfirmed, scans)})`,
      `${k.postConfirmed} (${percent(k.postConfirmed, k.texted)})`,
    ],
    [
      'Live on Google (matched)',
      `${qrMatched} (${percent(qrMatched, scans)})`,
      `${k.matched} (${percent(k.matched, k.texted)})`,
    ],
    ['Private feedback', String(q.outcomes.privateFeedback), String(k.privateFeedback)],
    [
      'Cost per review',
      '$0 (printed)',
      t.cost?.perReviewUsd != null ? usd(t.cost.perReviewUsd) : '—',
    ],
  ];

  return (
    <div className={a.stack}>
      {toolbar}
      <div className={a.callout}>
        A scan is a patient who already chose to start; a text reaches everyone with a confirmed
        visit. So text converts a smaller share but usually produces far more reviews.
      </div>
      <AnalyticsCard
        title="Side by side"
        sub="Printed QR codes keep working while a practice texts, so both can run at once"
      >
        <div className={a.tableWrap}>
          <table className={a.table}>
            <thead>
              <tr>
                <th />
                <th className={a.right}>Link &amp; QR</th>
                <th className={a.right}>Text</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, qrValue, textValue]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td className={a.right}>
                    <span className={a.cmpQr}>{qrValue}</span>
                  </td>
                  <td className={a.right}>
                    <span className={a.cmpText}>{textValue}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AnalyticsCard>
      <WeeklyByMethodCard
        weekly={t.weekly}
        title="Google reviews per week, by method"
        sub="New reviews matched to a request in the last 13 weeks"
      />
    </div>
  );
}
